import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Popover, TextField } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Extension } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

function normaliseLink(value) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
  if (trimmed.includes("@") && !trimmed.includes(" ")) return `mailto:${trimmed}`;
  return `https://${trimmed}`;
}

function ToolButton({ active, icon, label, onClick, disabled }) {
  return (
    <Button
      size="small"
      variant={active ? "contained" : "outlined"}
      startIcon={icon}
      disabled={disabled}
      aria-pressed={active}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}

export default function LegalWordingEditor({
  value,
  baselineKey,
  onChange,
  onBaseline,
  disabled = false,
}) {
  const onChangeRef = useRef(onChange);
  const onBaselineRef = useRef(onBaseline);
  const valueRef = useRef(value);
  const applyingRef = useRef(false);
  onChangeRef.current = onChange;
  onBaselineRef.current = onBaseline;
  valueRef.current = value;

  const [linkAnchor, setLinkAnchor] = useState(null);
  const [linkValue, setLinkValue] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        strike: false,
        horizontalRule: false,
        orderedList: false,
      }),
      TextStyle,
      FontSize,
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ["http", "https", "mailto"],
      }),
    ],
    content: value || "",
    editable: !disabled,
    editorProps: {
      attributes: {
        "aria-label": "Legal wording",
        class: "legal-wording-editor",
      },
    },
    onUpdate: ({ editor: current }) => {
      if (!applyingRef.current) onChangeRef.current(current.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    applyingRef.current = true;
    editor.commands.setContent(valueRef.current || "", false);
    editor.commands.setTextSelection(1);
    const html = editor.getHTML();
    applyingRef.current = false;
    onBaselineRef.current(html);
  }, [editor, baselineKey]);

  const openLink = (event) => {
    const previous = editor?.getAttributes("link").href || "";
    setLinkValue(previous);
    setLinkAnchor(event.currentTarget);
  };

  const applyLink = () => {
    const href = normaliseLink(linkValue);
    if (!editor) return;
    if (!href) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    setLinkAnchor(null);
  };

  const smallActive = editor?.isActive("textStyle", { fontSize: "8pt" });

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.brand.border}`,
        borderRadius: 1,
        overflow: "hidden",
        "&:focus-within": { borderColor: theme.palette.primary.main },
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          p: 1,
          borderBottom: `1px solid ${theme.palette.brand.border}`,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <ToolButton
          label="Bold"
          icon={<FormatBoldIcon />}
          active={Boolean(editor?.isActive("bold"))}
          disabled={disabled || !editor}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolButton
          label="Italic"
          icon={<FormatItalicIcon />}
          active={Boolean(editor?.isActive("italic"))}
          disabled={disabled || !editor}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <ToolButton
          label="Small print"
          icon={<FormatSizeIcon />}
          active={Boolean(smallActive)}
          disabled={disabled || !editor}
          onClick={() => {
            if (smallActive) editor.chain().focus().unsetFontSize().run();
            else editor.chain().focus().setFontSize("8pt").run();
          }}
        />
        <ToolButton
          label="Bullets"
          icon={<FormatListBulletedIcon />}
          active={Boolean(editor?.isActive("bulletList"))}
          disabled={disabled || !editor}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <ToolButton
          label="Link"
          icon={<LinkIcon />}
          active={Boolean(editor?.isActive("link"))}
          disabled={disabled || !editor}
          onClick={openLink}
        />
      </Box>
      <Box
        sx={{
          maxHeight: "calc(100vh - 360px)",
          minHeight: 240,
          overflowY: "auto",
          px: 2,
          py: 1,
          "& .legal-wording-editor": {
            minHeight: 220,
            fontSize: 15,
            lineHeight: 1.5,
            outline: "none",
          },
          "& .legal-wording-editor p": { margin: "0.35em 0", minHeight: "1.15em" },
          "& .legal-wording-editor ul": { margin: "0.35em 0", paddingLeft: 3 },
          "& .legal-wording-editor a": { color: "primary.main" },
          '& .legal-wording-editor span[style*="8pt"]': { fontSize: "0.86em" },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ display: "flex", gap: 1, p: 1.5, alignItems: "center" }}>
          <TextField
            size="small"
            label="Web address or email"
            value={linkValue}
            onChange={(event) => setLinkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") applyLink();
            }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="contained" startIcon={<LinkIcon />} onClick={applyLink}>
            Apply link
          </Button>
          <Button
            variant="outlined"
            startIcon={<LinkOffIcon />}
            onClick={() => {
              editor?.chain().focus().unsetLink().run();
              setLinkAnchor(null);
            }}
          >
            Remove link
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
